// Zone maps: collision data shared by client and server. Art lives on the client only.
import { SUB } from './constants.js';
import town from './data/town.json';
import { INTERIORS, interiorMap } from './interiors.js';
import { ZONES } from './zones.js';

export interface ZoneMapData {
  id: string;
  name: string;
  width: number;      // tiles
  height: number;     // tiles
  tileSize: number;   // pixels
  spawn: [number, number];   // tile
  /** One character per tile: '.' open ground, '#' wall (trees, houses), '~' water, '1'-'9' something you can
   *  jump onto, that many times 4 pixels high (crates, rocks, fences, benches). */
  collision: string[];
  npcs?: { name: string; at: [number, number] }[];
  mounts?: boolean;          // mounts allowed here (open world)
  flying?: boolean;          // flying allowed here
}

/** Heights in sub-pixels. A wall stops anyone below WALL_H; only flyers get that high. Water needs flight. */
export const WALL_H = 48 * SUB, WATER_FLY_H = 8 * SUB, STEP_H = 3 * SUB;
const WALL = 255, WATER = 254;

export class ZoneMap {
  readonly id: string;
  readonly name: string;
  readonly w: number;
  readonly h: number;
  readonly ts: number;
  private readonly blocked: Uint8Array;
  /** Per tile: 0 ground, 1-9 platform height (x4 px), WALL, WATER. */
  private readonly kind: Uint8Array;
  readonly data: ZoneMapData;
  readonly mounts: boolean;
  readonly flying: boolean;

  constructor(data: ZoneMapData) {
    this.data = data;
    this.id = data.id; this.name = data.name; this.w = data.width; this.h = data.height; this.ts = data.tileSize;
    this.mounts = !!data.mounts; this.flying = !!data.flying;
    this.blocked = new Uint8Array(this.w * this.h); this.kind = new Uint8Array(this.w * this.h);
    data.collision.forEach((row, y) => {
      for (let x = 0; x < this.w; x++) {
        const c = row[x] ?? '#', i = y * this.w + x;
        this.blocked[i] = c === '.' ? 0 : 1;
        this.kind[i] = c === '.' ? 0 : c === '~' ? WATER : c >= '1' && c <= '9' ? c.charCodeAt(0) - 48 : WALL;
      }
    });
  }

  private kindSub(sx: number, sy: number): number {
    const span = this.ts * SUB, tx = Math.floor(sx / span), ty = Math.floor(sy / span);
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return -1;
    return this.kind[ty * this.w + tx];
  }
  /** Can a point at height z (sub-pixels) be here? */
  private passSub(sx: number, sy: number, z: number, flying: boolean): boolean {
    const k = this.kindSub(sx, sy);
    if (k < 0) return false;                       // the edge of the map stops everyone
    if (k === 0) return true;
    if (k === WALL) return flying && z >= WALL_H;
    if (k === WATER) return flying && z >= WATER_FLY_H;
    return z + STEP_H >= k * 4 * SUB;              // a crate, a rock: only if you're up at its height
  }
  /** Feet footprint (same as canStandSub) at height z. */
  fitsSub(sx: number, sy: number, z: number, flying: boolean): boolean {
    return this.passSub(sx - 5 * SUB, sy - 4 * SUB, z, flying) && this.passSub(sx + 5 * SUB, sy - 4 * SUB, z, flying)
      && this.passSub(sx - 5 * SUB, sy, z, flying) && this.passSub(sx + 5 * SUB, sy, z, flying);
  }
  /** What you'd land on here: the highest surface under your feet. Flyers can't land on water or walls:
   *  `landable` says whether this floor is somewhere you can stand. */
  floorSub(sx: number, sy: number): { h: number; landable: boolean } {
    let h = 0, landable = true;
    for (const [dx, dy] of FOOT) {
      const k = this.kindSub(sx + dx, sy + dy);
      if (k < 0) continue;
      if (k === WALL) { h = Math.max(h, WALL_H); landable = false; }
      else if (k === WATER) { h = Math.max(h, WATER_FLY_H); landable = false; }
      else h = Math.max(h, k * 4 * SUB);
    }
    return { h, landable };
  }

  /** Is the tile containing this point (in sub-pixels) open? Outside the map counts as blocked. */
  walkableSub(sx: number, sy: number): boolean {
    const span = this.ts * SUB;
    const tx = Math.floor(sx / span), ty = Math.floor(sy / span);
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return false;
    return this.blocked[ty * this.w + tx] === 0;
  }

  /** Feet footprint: 10 pixels wide, 4 deep, matching the single-player town. */
  canStandSub(sx: number, sy: number): boolean {
    return this.walkableSub(sx - 5 * SUB, sy - 4 * SUB) && this.walkableSub(sx + 5 * SUB, sy - 4 * SUB)
      && this.walkableSub(sx - 5 * SUB, sy) && this.walkableSub(sx + 5 * SUB, sy);
  }

  /** The nearest open standing spot to a tile, in sub-pixels. */
  nearestFreeSub(tx: number, ty: number): [number, number] {
    for (let r = 0; r < 8; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const x = ((tx + dx) * this.ts + 8) * SUB, y = ((ty + dy) * this.ts + 12) * SUB;
      if (this.canStandSub(x, y)) return [x, y];
    }
    return [(tx * this.ts + 8) * SUB, (ty * this.ts + 12) * SUB];
  }

  spawnSub(): [number, number] { return this.nearestFreeSub(this.data.spawn[0], this.data.spawn[1]); }
}

const FOOT: [number, number][] = [[-5 * SUB, -4 * SUB], [5 * SUB, -4 * SUB], [-5 * SUB, 0], [5 * SUB, 0]];

/** Every zone the game knows about. Dungeons and raids are instances: separate copies of one of these maps. */
export const MAPS: Record<string, ZoneMapData> = {
  town: town as ZoneMapData,
  ...Object.fromEntries(INTERIORS.map(r => [r.id, interiorMap(r)])),
  ...Object.fromEntries(ZONES.filter(z => z.map).map(z => [z.id, z.map])),
};

const cache = new Map<string, ZoneMap>();
export function getMap(id: string): ZoneMap {
  let m = cache.get(id);
  if (!m) { const d = MAPS[id]; if (!d) throw new Error('unknown map ' + id); m = new ZoneMap(d); cache.set(id, m); }
  return m;
}
